import { DeviceType, NotificationType } from "@virtualprogaming/js-public-sdk";
import { onMessage } from "firebase/messaging";
import { partial } from "lodash";
import React, { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { useHistory } from "react-router-dom";

import { NOTIFICATIONS_BY_USER } from "~/constants";
import { messaging, requestForToken } from "~/firebase";
import { useFcmSubscribe } from "~/queries";
import {
  SelectedWindows,
  useChatContext,
  useNotificationsSettings,
  useToast,
} from "~/ui/context";
import {
  convertEmojisToIcons,
  parseNotificationType,
  shouldNotShowChatNotification,
  shouldNotShowCommentNotification,
  shouldNotShowFollowNotification,
  shouldNotShowLikeNotification,
  shouldShowAllNotifications,
  useIsLoggedIn,
  useScreenSm,
} from "~/utils";

export const Notification: React.FC = ({ children }) => {
  const toast = useToast();
  const [hasSubscribed, setHasSubscribed] = useState(false);
  const { mutateAsync: subscribe } = useFcmSubscribe();
  const isLoggedIn = useIsLoggedIn();
  const queryClient = useQueryClient();
  const { notificationsSettings } = useNotificationsSettings();
  const { push } = useHistory();
  const isMobile = useScreenSm();
  const {
    selectedChat,
    setIsChatOpen,
    setSelectedChat,
    setSelectedWindow,
    isChatOpen,
  } = useChatContext();

  const handleNotificationsClick = (
    title: string,
    type: NotificationType | null,
    id: string | null
  ) => {
    if (!id) return;

    if (type === NotificationType.MessageReceived) {
      setIsChatOpen(true);
      setSelectedChat(Number(id));
      setSelectedWindow(SelectedWindows.Conversation);
    } else if (
      type === NotificationType.LikedPost ||
      type === NotificationType.PostReply ||
      type === NotificationType.PostTag
    ) {
      if (title.includes("your recruitment")) {
        push(`/recruitment-post/${id}`);
      } else {
        push(`/social-post/${id}`);
      }
      setIsChatOpen(false);
    } else if (type === NotificationType.UserFollow) {
      push(`/user/${id}`);
      setIsChatOpen(false);
    } else {
      setIsChatOpen(false);
      push("/notifications");
    }
  };

  useEffect(() => {
    const getToken = async () => {
      const currentToken = await requestForToken();
      if (currentToken) {
        subscribe({
          device_token: currentToken,
          device_type: DeviceType.Web,
        }).then(() => {
          setHasSubscribed(true);
        });
      }
    };

    if (isLoggedIn && !hasSubscribed && !isMobile) {
      getToken();
    }
  }, [hasSubscribed, isLoggedIn, isMobile, subscribe]);

  onMessage(messaging, async (payload) => {
    if (payload.notification?.title === "Subscribed") return null;
    const content = convertEmojisToIcons(payload.notification?.body ?? "");
    const { type: typeString, obj_id: id } = payload.data ?? {};
    const type: NotificationType | undefined = parseNotificationType(
      typeString
    );

    if (
      (type && !shouldShowAllNotifications(notificationsSettings)) ||
      (type &&
        (shouldNotShowLikeNotification(notificationsSettings, type) ||
          shouldNotShowChatNotification(notificationsSettings, type) ||
          shouldNotShowCommentNotification(notificationsSettings, type) ||
          shouldNotShowFollowNotification(notificationsSettings, type)))
    ) {
      await queryClient.invalidateQueries([NOTIFICATIONS_BY_USER]);
      return null;
    } else {
      if (
        type === NotificationType.MessageReceived &&
        Number(id) === selectedChat &&
        isChatOpen
      )
        return null;
      if (!type) return null;
      toast({
        message: (
          <div
            className="flex flex-col items-start mg-l-md pd-md"
            onClick={partial(
              handleNotificationsClick,
              payload?.notification?.title ?? "",
              type,
              id
            )}
          >
            <h6>{payload?.notification?.title}</h6>
            {content && (
              <p className="word-break-all">
                {content.substring(0, 100)}
                {content.length > 100 && "..."}
              </p>
            )}
          </div>
        ),
      });
      await queryClient.invalidateQueries([NOTIFICATIONS_BY_USER]);
    }
  });

  return <>{children}</>;
};
